import type { Booking } from '../../lib/types'
import { Button, Spinner, DataTable, DataTableHeader, DataTableBody } from '@perch/ui'
import { BookingRow } from './BookingRow'

interface CollapsibleBookingsSectionProps {
  title: string
  total: number
  bookings: Booking[]
  expanded: boolean
  loading: boolean
  onToggle: () => void
  onLoadMore: () => void
  onBookingClick: (booking: Booking) => void
}

export function CollapsibleBookingsSection({
  title,
  total,
  bookings,
  expanded,
  loading,
  onToggle,
  onLoadMore,
  onBookingClick,
}: CollapsibleBookingsSectionProps) {
  if (total <= 0) return null

  return (
    <section>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 card-surface rounded-2xl hover:shadow-sm transition-all"
      >
        <span className="text-sm font-medium text-stone-500 dark:text-stone-400">
          {title}
          <span className="ml-2 text-stone-400 dark:text-stone-500">{total}</span>
        </span>
        <svg
          className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3">
          {loading && bookings.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-stone-400">
                <Spinner />
                <span className="text-sm">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <DataTable footer={bookings.length < total ? (
                <div className="p-4 border-t border-stone-200 dark:border-stone-800 text-center">
                  <Button onClick={onLoadMore} disabled={loading} variant="ghost" size="sm">
                    {loading ? 'Loading...' : `Load more (${bookings.length} of ${total})`}
                  </Button>
                </div>
              ) : undefined}>
                <DataTableHeader>
                  <th>User</th>
                  <th>Airline</th>
                  <th>Route</th>
                  <th>PNR</th>
                  <th>Paid</th>
                  <th>Last Checked</th>
                </DataTableHeader>
                <DataTableBody emptyMessage="No bookings" colSpan={6}>
                  {bookings.map(booking => (
                    <BookingRow key={booking.id} booking={booking} showStatus={false} onClick={() => onBookingClick(booking)} />
                  ))}
                </DataTableBody>
              </DataTable>
            </>
          )}
        </div>
      )}
    </section>
  )
}
